import clsx from "clsx";
import { useCallback, useEffect, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { useSwiper } from "swiper/react";

import { Icon } from "../icon";
import { StyledButton } from "../styled-button";
import { Body } from "../typography/body";

import s from "./slider-nav-buttons.module.scss";

type UISwiperNavButtonsProps = {
  variant: "light" | "dark";
  buttonType?: "primary" | "secondary";
  totalSlides?: number;
  currentIndex?: number;
  className?: string;
  slidePrev: () => void;
  slideNext: () => void;
  disabledPrev?: boolean;
  disabledNext?: boolean;
};

type SwiperNavButtonsProps = Omit<
  UISwiperNavButtonsProps,
  "slidePrev" | "slideNext" | "disabledPrev" | "disabledNext"
> & {
  swiperRef?: React.RefObject<SwiperType | null>;
};

export const SwiperNavButtons = (props: SwiperNavButtonsProps) => {
  const { swiperRef } = props;

  const [isExternal, setIsExternal] = useState(false);

  useEffect(() => {
    setIsExternal(!!swiperRef);
  }, []);

  if (isExternal) {
    return <SwiperNavButtonsExternal {...props} />;
  }

  return <SwiperNavButtonsInternal {...props} />;
};

export const UISwiperNavButtons = ({
  variant,
  totalSlides,
  currentIndex,
  className,
  slidePrev,
  slideNext,
  buttonType = "primary",
}: UISwiperNavButtonsProps) => {
  return (
    <div className={clsx(s.root, className)}>
      {currentIndex !== undefined && totalSlides && (
        <Body
          size="uppercase"
          className={s.counters}
        >{`${currentIndex + 1}/${totalSlides}`}</Body>
      )}
      <div className={s.buttons}>
        <StyledButton
          variant={`${buttonType}-${variant}`}
          onClick={slidePrev}
          aria-label="prev slide"
          size="xs"
        >
          <Icon name="arrow-left" />
        </StyledButton>
        <StyledButton
          variant={`${buttonType}-${variant}`}
          onClick={slideNext}
          aria-label="next slide"
          size="xs"
        >
          <Icon name="arrow-right" />
        </StyledButton>
      </div>
    </div>
  );
};

const SwiperNavButtonsInternal = (props: SwiperNavButtonsProps) => {
  const { totalSlides, currentIndex, className, variant, buttonType } = props;
  const swiper = useSwiper();

  const slidePrev = useCallback(() => {
    swiper.slidePrev();
  }, [swiper]);

  const slideNext = useCallback(() => {
    swiper.slideNext();
  }, [swiper]);

  return (
    <UISwiperNavButtons
      variant={variant}
      buttonType={buttonType}
      totalSlides={totalSlides}
      currentIndex={currentIndex}
      className={className}
      slidePrev={slidePrev}
      slideNext={slideNext}
    />
  );
};

const SwiperNavButtonsExternal = ({
  totalSlides,
  currentIndex,
  className,
  swiperRef,
  variant,
  buttonType,
}: SwiperNavButtonsProps) => {
  const swiper = swiperRef?.current;

  const slidePrev = useCallback(() => {
    swiper?.slidePrev();
  }, [swiper]);

  const slideNext = useCallback(() => {
    swiper?.slideNext();
  }, [swiper]);

  return (
    <UISwiperNavButtons
      variant={variant}
      buttonType={buttonType}
      totalSlides={totalSlides}
      currentIndex={currentIndex}
      className={className}
      slidePrev={slidePrev}
      slideNext={slideNext}
    />
  );
};
